<template>
    <div>
        <h3>Ansatte<template v-if="competitionName"> - {{ competitionName }}</template></h3>
        <div class="table" v-if="roster.length">
            <table>
                <thead>
                    <tr>
                        <th class="table__header--name">Navn</th>
                        <th>E-post</th>
                        <th style="text-align: center;">Administrator</th>
                        <th style="text-align: center;">Kontaktperson</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in roster" :key="row.person_id" class="table__entry">
                        <td class="table__name">
                            <a href="#" class="table__secondary-link" @click.prevent="openModal(row)">{{ row.Navn }} {{ row.Etternavn }}</a>
                        </td>
                        <td>{{ row.e_mail }}</td>
                        <td style="text-align: center;">
                            <i v-if="row.is_admin" class="pi pi-shield"></i>
                        </td>
                        <td style="text-align: center;">
                            <i v-if="row.is_contact_admin" class="pi pi-envelope" title="Kontaktperson for bedriften"></i>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <p v-else>Ingen ansatte ennå.</p>

        <div class="form__subsection">
            <h3>Inviter ansatte</h3>
            <p class="invite-hint">Skriv inn én e-postadresse per linje for å sende invitasjon til flere ansatte samtidig.</p>
            <textarea class="invite-textarea" v-model="inviteEmails" placeholder="En e-postadresse per linje" rows="4"></textarea>
            <div>
                <button type="button" class="action" :disabled="!inviteEmailList.length || busy" @click="openInviteModal">
                    <i class="pi pi-envelope"></i>
                    Send invitasjoner
                </button>
            </div>

            <label class="invite-link-label">
                <span class="invite-link-label__text">Invitasjonslenke</span>
                <input type="text" class="invite-textarea invite-link-input" :value="inviteLink" readonly @click="$event.target.select()">
            </label>
        </div>

        <Teleport to="body">
            <div class="st-modal" :class="{ 'st-modal--active': modalOpen }">
                <div class="st-modal__content">
                    <div class="st-modal__header">
                        <div class="hero__header">{{ activeRow?.Navn }} {{ activeRow?.Etternavn }}</div>
                    </div>
                    <div class="st-modal__text">
                        <div v-if="modalError" class="form__error">{{ modalError }}</div>

                        <template v-if="confirmingRemove">
                            <p>Er du sikker på at du vil fjerne {{ activeRow?.Navn }} {{ activeRow?.Etternavn }} fra bedriften?</p>
                            <div class="mypage__editbar">
                                <button type="button" class="action" :disabled="busy" @click="remove(activeRow)">
                                    <i class="pi pi-trash"></i>
                                    {{ busy ? 'Fjerner...' : 'Bekreft' }}
                                </button>
                                <button type="button" class="action action--hollow" :disabled="busy" @click="confirmingRemove = false">Avbryt</button>
                            </div>
                        </template>
                        <template v-else>
                            <p>{{ activeRow?.e_mail }}</p>
                            <div class="mypage__editbar">
                                <button type="button" class="action" :disabled="busy" @click="toggleAdmin(activeRow)">
                                    <i class="pi pi-shield"></i>
                                    {{ activeRow?.is_admin ? 'Fjern admin' : 'Gjør til admin' }}
                                </button>
                                <button v-if="!activeRow?.is_contact_admin" type="button" class="action" :disabled="busy" @click="setContactPerson(activeRow)">
                                    <i class="pi pi-envelope"></i>
                                    Gjør til kontaktperson
                                </button>
                                <button v-if="!activeRow?.is_admin && !activeRow?.is_contact_admin" type="button" class="action" :disabled="busy" @click="confirmingRemove = true">
                                    <i class="pi pi-trash"></i>
                                    Fjern fra bedrift
                                </button>
                            </div>
                            <p v-if="activeRow?.is_admin || activeRow?.is_contact_admin" style="font-size:0.875rem; color:#6c757d;">
                                Kan ikke fjernes fra bedriften mens personen er administrator eller kontaktperson.
                            </p>
                            <div class="mypage__editbar">
                                <button type="button" class="action action--hollow" @click="closeModal">
                                    Lukk
                                </button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </Teleport>

        <Teleport to="body">
            <div class="st-modal" :class="{ 'st-modal--active': inviteModalOpen }">
                <div class="st-modal__content">
                    <div class="st-modal__header">
                        <div class="hero__header">Inviter ansatte</div>
                    </div>
                    <div class="st-modal__text">
                        <div v-if="inviteError" class="form__error">
                            {{ inviteError }}
                            <template v-for="(email, i) in inviteInvalidEmails" :key="email">
                                <template v-if="i > 0">, </template>&quot;<strong>{{ email }}</strong>&quot;
                            </template>
                        </div>

                        <p v-if="!inviteOutcome">
                            Send invitasjon til {{ inviteEmailList.length }}
                            {{ inviteEmailList.length === 1 ? 'e-postadresse' : 'e-postadresser' }}?
                        </p>

                        <div v-if="!inviteOutcome" class="mypage__editbar">
                            <button type="button" class="action" :disabled="busy" @click="sendInvites">
                                {{ busy ? 'Sender...' : 'Bekreft' }}
                            </button>
                            <button type="button" class="action action--hollow" :disabled="busy" @click="closeInviteModal">Avbryt</button>
                        </div>

                        <template v-else-if="inviteOutcome === 'sent'">
                            <i class="pi pi-check-circle" style="font-size: 2rem; color: green;"></i>
                            <div class="mypage__editbar">
                                <button type="button" class="action action--hollow" @click="closeInviteModal">Lukk</button>
                            </div>
                        </template>

                        <template v-else>
                            <div class="mypage__editbar">
                                <button type="button" class="action action--hollow" @click="closeInviteModal">Lukk</button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    csrfToken: { type: String, required: true },
    competitionName: { type: String, default: '' },
    initialRoster: { type: Array, default: () => [] },
    inviteLink: { type: String, default: '' },
});

const roster = ref(props.initialRoster);
const inviteEmails = ref('');
const busy = ref(false);
const modalError = ref(null);

const modalOpen = ref(false);
const activeRow = ref(null);
const confirmingRemove = ref(false);

// Mirrors the server's own splitting (Xhr\Frontend\Company\AdminController::invite()
// -> CompanyDashboardRepository::inviteEmployees()) - one address per line,
// blank lines ignored - just for the "send to N addresses?" count.
const inviteEmailList = computed(() => inviteEmails.value.split(/\r\n|\r|\n/).map((line) => line.trim()).filter(Boolean));

const inviteModalOpen = ref(false);
// null = not yet attempted, 'sent' = success, 'failed' = send failed.
const inviteOutcome = ref(null);
const inviteError = ref(null);
const inviteInvalidEmails = ref([]);

function openModal(row) {
    activeRow.value = row;
    modalError.value = null;
    confirmingRemove.value = false;
    modalOpen.value = true;
}

function closeModal() {
    modalOpen.value = false;
    activeRow.value = null;
    confirmingRemove.value = false;
}

function openInviteModal() {
    inviteError.value = null;
    inviteInvalidEmails.value = [];
    inviteOutcome.value = null;
    inviteModalOpen.value = true;
}

function closeInviteModal() {
    inviteModalOpen.value = false;
    // Only clear the textarea once the invitations actually went out - if
    // the admin cancelled or the send failed, their typed-in addresses
    // should still be there to fix/retry.
    if (inviteOutcome.value === 'sent') {
        inviteEmails.value = '';
    }
    inviteOutcome.value = null;
}

function headers() {
    return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': props.csrfToken,
    };
}

// errorRef lets each caller route a failure to its own error message slot -
// modalError for the roster modal's own actions, inviteError for the
// invite modal's. invalidEmailsRef (invite only) receives the addresses
// that failed validation so the template can render them bold/quoted.
async function post(url, body, errorRef, invalidEmailsRef) {
    busy.value = true;
    errorRef.value = null;
    if (invalidEmailsRef) {
        invalidEmailsRef.value = [];
    }
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: headers(),
            body: body ? JSON.stringify(body) : undefined,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
            errorRef.value = data.message || 'Noe gikk galt, prøv igjen.';
            if (invalidEmailsRef) {
                invalidEmailsRef.value = data.invalidEmails || [];
            }
            return null;
        }
        return data;
    } catch (e) {
        errorRef.value = 'Noe gikk galt, prøv igjen.';
        return null;
    } finally {
        busy.value = false;
    }
}

async function toggleAdmin(row) {
    const data = await post(`/xhr/frontend/companies/roster/${row.person_id}/admin`, { is_admin: !row.is_admin }, modalError);
    if (data) {
        roster.value = data.roster;
        closeModal();
    }
}

async function setContactPerson(row) {
    const data = await post(`/xhr/frontend/companies/roster/${row.person_id}/contact-admin`, undefined, modalError);
    if (data) {
        roster.value = data.roster;
        closeModal();
    }
}

async function remove(row) {
    const data = await post(`/xhr/frontend/companies/roster/${row.person_id}/remove`, undefined, modalError);
    if (data) {
        roster.value = data.roster;
        closeModal();
    }
}

async function sendInvites() {
    const data = await post('/xhr/frontend/companies/invite', { emails: inviteEmails.value }, inviteError, inviteInvalidEmails);
    inviteOutcome.value = data ? 'sent' : 'failed';
}
</script>

<style scoped>
.st-modal__text {
    text-align: center;
}

/* Textarea isn't covered by .form input[type=...]'s styling (that selector
requires a .form/.content-width form ancestor this widget doesn't have, and
doesn't include textarea anyway) - styled directly here to match the same
look. */
.invite-hint {
    margin: 0 0 0.5rem;
    font-size: 0.9rem;
    color: rgba(48, 57, 57, 0.65);
}
.invite-textarea {
    display: block;
    width: 100%;
    box-sizing: border-box;
    border-radius: 4px;
    border: 1px solid #D3D9DE;
    background-color: #F2F5F8;
    color: #303939;
    font-size: 1rem;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    resize: vertical;
}
.invite-textarea::placeholder {
    color: inherit;
}

.invite-link-label {
    display: block;
    margin-top: 1.5rem;
}
.invite-link-label__text {
    display: block;
    font-weight: bold;
    margin-bottom: 0.4rem;
}
.invite-link-input {
    margin-bottom: 0;
    cursor: pointer;
}
</style>
